/**
 * LocalStorage State Management Utility
 * Stage 4: Backend-Frontend Integration
 */

import { AppState, AvatarGeneration, FilterState, RateLimitStatus } from '../types/api';
import { FileProcessor } from './fileProcessor';

const STORAGE_KEYS = {
  APP_STATE: 'prompt2avatar_app_state',
  USER_PREFERENCES: 'prompt2avatar_user_preferences',
  LIKED_IMAGES: 'prompt2avatar_liked_images'
};

const MAX_GENERATION_HISTORY = 10; // Keep last 10 generations

/**
 * LocalStorage wrapper with error handling
 */
class LocalStorageManager {
  
  /**
   * Save data to localStorage with error handling
   */
  private setItem(key: string, value: any): boolean {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error(`Failed to save to localStorage (${key}):`, error);
      return false;
    }
  }

  /**
   * Get data from localStorage with error handling
   */
  private getItem<T>(key: string, defaultValue: T): T {
    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue;
      }
      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`Failed to read from localStorage (${key}):`, error);
      return defaultValue;
    }
  }

  /**
   * Remove item from localStorage
   */
  private removeItem(key: string): boolean {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Failed to remove from localStorage (${key}):`, error);
      return false;
    }
  }

  /**
   * Get complete app state
   */
  getAppState(): AppState {
    return this.getItem<AppState>(STORAGE_KEYS.APP_STATE, {
      generationHistory: [],
      userPreferences: {
        style: {
          type: 'photorealistic',
          ratio: 'vertical',
          scene: 'mid-closeup'
        },
        age: 'any',
        gender: 'any',
        hair: [],
        background: 'any'
      },
      lastUpdated: new Date().toISOString()
    });
  }

  /**
   * Save complete app state
   */
  saveAppState(state: AppState): boolean {
    const stateWithTimestamp = {
      ...state,
      lastUpdated: new Date().toISOString()
    };
    return this.setItem(STORAGE_KEYS.APP_STATE, stateWithTimestamp);
  }

  /**
   * Get user filter preferences
   */
  getUserPreferences(): FilterState {
    return this.getItem<FilterState>(STORAGE_KEYS.USER_PREFERENCES, {
      style: {
        type: 'photorealistic',
        ratio: 'vertical',
        scene: 'mid-closeup'
      },
    age: 'any',
    gender: 'any',
    hair: [],
    background: 'any',
    model: 'nano-banana'
  });
  }

  /**
   * Save user filter preferences
   */
  saveUserPreferences(preferences: FilterState): boolean {
    const success = this.setItem(STORAGE_KEYS.USER_PREFERENCES, preferences);
    
    // Also update in app state
    const appState = this.getAppState();
    appState.userPreferences = preferences;
    this.saveAppState(appState);
    
    return success;
  }

  /**
   * Get generation history from app state (single source of truth)
   */
  getGenerationHistory(): AvatarGeneration[] {
    const appState = this.getAppState();
    const history = appState.generationHistory || [];
    
    // Migrate old generations without timestamps
    const migratedHistory = history.map((gen, index) => {
      if (!gen.timestamp) {
        console.warn(`Generation ${gen.id} missing timestamp, assigning fallback`);
        return {
          ...gen,
          // Use reverse index as fallback (oldest first, then reverse in sort)
          timestamp: new Date(Date.now() - (history.length - index) * 60000).toISOString()
        };
      }
      return gen;
    });
    
    // Ensure proper sorting: most recent first
    return migratedHistory.sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeB - timeA; // Most recent first
    });
  }

  /**
   * Get generation history with validated images
   * Filters out generations with no valid images and ensures proper ordering
   */
  async getValidatedGenerationHistory(): Promise<AvatarGeneration[]> {
    const history = this.getGenerationHistory(); // Already sorted
    const validatedHistory: AvatarGeneration[] = [];

    for (const generation of history) {
      if (!generation.s3Urls || generation.s3Urls.length === 0) {
        continue; // Skip generations with no URLs
      }

      // Validate all URLs for this generation
      const validUrls = await FileProcessor.filterValidImageUrls(generation.s3Urls, 3000);
      
      if (validUrls.length > 0) {
        // Keep generation but only with valid URLs
        validatedHistory.push({
          ...generation,
          s3Urls: validUrls
        });
      }
    }

    // Sort again after validation to ensure consistency
    validatedHistory.sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeB - timeA; // Most recent first
    });

    // If we filtered out any generations OR migrated timestamps, update app state
    if (validatedHistory.length !== history.length || 
        history.some(g => !g.timestamp)) {
      // console.log(`Updated app state: filtered ${history.length - validatedHistory.length} invalid generations`);
      
      const appState = this.getAppState();
      this.saveAppState({
        ...appState,
        generationHistory: validatedHistory
      });
    }

    return validatedHistory;
  }

  /**
   * Save a new generation to history with proper ordering
   * Single atomic operation - preserves rate limit and all other state
   */
  saveGeneration(generation: AvatarGeneration): boolean {
    // Get current app state (includes rate limit, preferences, etc.)
    const appState = this.getAppState();
    
    // Get current history from app state
    let history = appState.generationHistory || [];
    
    // Remove any existing generation with the same ID (avoid duplicates)
    history = history.filter(g => g.id !== generation.id);
    
    // Add new generation to beginning
    history.unshift(generation);
    
    // Sort by timestamp to ensure correct order
    history.sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeB - timeA; // Most recent first
    });
    
    // Keep only the last MAX_GENERATION_HISTORY items
    const trimmedHistory = history.slice(0, MAX_GENERATION_HISTORY);
    
    // Single atomic save - updates everything at once
    // Automatically preserves rate limit, preferences, etc.
    return this.saveAppState({
      ...appState,
      generationHistory: trimmedHistory,
      lastGenerationId: generation.id,
      lastUpdated: new Date().toISOString()
    });
  }

  /**
   * Save generation and rate limit atomically in a single operation
   * This prevents race conditions when updating both at once
   */
  saveGenerationWithRateLimit(generation: AvatarGeneration, rateLimit: RateLimitStatus): boolean {
    // Get current app state once
    const appState = this.getAppState();
    
    // Get current history
    let history = appState.generationHistory || [];
    
    // Remove duplicates
    history = history.filter(g => g.id !== generation.id);
    
    // Add new generation
    history.unshift(generation);
    
    // Sort by timestamp
    history.sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeB - timeA; // Most recent first
    });
    
    // Trim to max
    const trimmedHistory = history.slice(0, MAX_GENERATION_HISTORY);
    
    // Single atomic save - updates BOTH generation and rate limit together
    return this.saveAppState({
      ...appState,
      generationHistory: trimmedHistory,
      lastGenerationId: generation.id,
      rateLimitStatus: rateLimit,
      lastUpdated: new Date().toISOString()
    });
  }

  /**
   * Get rate limit status from app state
   */
  getRateLimitStatus(): RateLimitStatus | null {
    const appState = this.getAppState();
    return appState.rateLimitStatus || null;
  }

  /**
   * Save rate limit status to app state
   */
  saveRateLimitStatus(status: RateLimitStatus): boolean {
    // Get current app state to preserve other data
    const appState = this.getAppState();
    
    // Update app state with new rate limit (preserves generations)
    return this.saveAppState({
      ...appState,
      rateLimitStatus: status
    });
  }

  /**
   * Clear all stored data
   */
  clearAll(): boolean {
    const keys = Object.values(STORAGE_KEYS);
    let success = true;
    
    keys.forEach(key => {
      if (!this.removeItem(key)) {
        success = false;
      }
    });
    
    return success;
  }

  /**
   * Clear only generation history
   */
  clearGenerationHistory(): boolean {
    // Get current app state to preserve rate limit
    const appState = this.getAppState();
    
    // Single atomic update - clears history while preserving rate limit
    return this.saveAppState({
      ...appState,
      generationHistory: [],
      lastGenerationId: undefined
    });
  }

  /**
   * Get storage usage info
   */
  getStorageInfo() {
    const keys = Object.values(STORAGE_KEYS);
    const info: Record<string, any> = {};
    
    keys.forEach(key => {
      try {
        const item = localStorage.getItem(key);
        info[key] = {
          exists: item !== null,
          size: item ? item.length : 0,
          sizeKB: item ? (item.length / 1024).toFixed(2) : 0
        };
      } catch (error) {
        info[key] = { error: error instanceof Error ? error.message : 'Unknown error' };
      }
    });
    
    return info;
  }

  /**
   * Get all liked image IDs
   */
  getLikedImages(): string[] {
    return this.getItem<string[]>(STORAGE_KEYS.LIKED_IMAGES, []);
  }

  /**
   * Toggle liked state for an image
   * Returns true if image is now liked, false if unliked
   */
  toggleLikedImage(imageId: string): boolean {
    const likedImages = this.getLikedImages();
    const index = likedImages.indexOf(imageId);
    
    if (index > -1) {
      // Image is already liked, remove it
      likedImages.splice(index, 1);
      this.setItem(STORAGE_KEYS.LIKED_IMAGES, likedImages);
      return false;
    } else {
      // Image is not liked, add it
      likedImages.push(imageId);
      this.setItem(STORAGE_KEYS.LIKED_IMAGES, likedImages);
      return true;
    }
  }

  /**
   * Check if an image is liked
   */
  isImageLiked(imageId: string): boolean {
    const likedImages = this.getLikedImages();
    return likedImages.includes(imageId);
  }

  /**
   * Migrate old data format (if needed)
   */
  migrateData(): boolean {
    try {
      const appState = this.getAppState();
      let needsSave = false;
      
      // Migration 1: Remove old GENERATION_HISTORY key (replaced by APP_STATE only)
      const oldGenHistoryKey = 'prompt2avatar_generation_history';
      const oldGenHistory = localStorage.getItem(oldGenHistoryKey);
      
      if (oldGenHistory) {
        console.log('🔄 Migrating old generation history to app state...');
        
        try {
          const oldHistory = JSON.parse(oldGenHistory);
          
          // Only migrate if app state doesn't have history or has less data
          if (!appState.generationHistory || appState.generationHistory.length < oldHistory.length) {
            appState.generationHistory = oldHistory;
            needsSave = true;
            // console.log(`✅ Migrated ${oldHistory.length} generations to app state`);
          }
          
          // Remove old key
          localStorage.removeItem(oldGenHistoryKey);
          // console.log('✅ Removed old generation history key');
        } catch (parseError) {
          console.error('Failed to parse old generation history:', parseError);
        }
      }
      
      // Migration 2: Remove old RATE_LIMIT_STATUS key (replaced by APP_STATE only)
      const oldRateLimitKey = 'prompt2avatar_rate_limit_status';
      const oldRateLimit = localStorage.getItem(oldRateLimitKey);
      
      if (oldRateLimit) {
        // console.log('🔄 Migrating old rate limit status to app state...');
        
        try {
          const oldRateLimitData = JSON.parse(oldRateLimit);
          
          // Only migrate if app state doesn't have rate limit
          if (!appState.rateLimitStatus) {
            appState.rateLimitStatus = oldRateLimitData;
            needsSave = true;
            // console.log('✅ Migrated rate limit status to app state');
          }
          
          // Remove old key
          localStorage.removeItem(oldRateLimitKey);
          // console.log('✅ Removed old rate limit status key');
        } catch (parseError) {
          console.error('Failed to parse old rate limit status:', parseError);
        }
      }
      
      // Save if we migrated anything
      if (needsSave) {
        this.saveAppState(appState);
      }
      
      // console.log('Data migration check completed');
      return true;
    } catch (error) {
      console.error('Data migration failed:', error);
      return false;
    }
  }
}

// Singleton instance
export const localStorageManager = new LocalStorageManager();

// Convenience functions
export const saveGeneration = (generation: AvatarGeneration) => 
  localStorageManager.saveGeneration(generation);

export const getGenerationHistory = () => 
  localStorageManager.getGenerationHistory();

export const saveUserPreferences = (preferences: FilterState) => 
  localStorageManager.saveUserPreferences(preferences);

export const getUserPreferences = () => 
  localStorageManager.getUserPreferences();

export const saveRateLimitStatus = (status: RateLimitStatus) => 
  localStorageManager.saveRateLimitStatus(status);

export const getRateLimitStatus = () => 
  localStorageManager.getRateLimitStatus();

export const getLikedImages = () => 
  localStorageManager.getLikedImages();

export const toggleLikedImage = (imageId: string) => 
  localStorageManager.toggleLikedImage(imageId);

export const isImageLiked = (imageId: string) => 
  localStorageManager.isImageLiked(imageId);
